import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { ApiKey, SdkUsageResponse } from '../types';
import toast from 'react-hot-toast';

export const useApiKeys = () => {
    return useQuery({
        queryKey: ['apiKeys'],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; data: ApiKey[] }>('/api-keys');
            return data.data;
        },
    });
};

export const useCreateApiKey = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (keyData: {
            name: string;
            environment: 'production' | 'test';
            ip_whitelist?: string[];
            expires_at?: string;
        }) => {
            const { data } = await api.post<{ success: boolean; data: ApiKey & { key?: string } }>('/api-keys', keyData);
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
            toast.success('API Key created successfully. Make sure to save it - it won\'t be shown again!');
        },
    });
};

export const useRotateApiKey = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (keyId: string) => {
            const { data } = await api.post<{ success: boolean; data: ApiKey & { key?: string } }>(`/api-keys/${keyId}/rotate`);
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
            toast.success('API Key rotated successfully');
        },
    });
};

export const useDeleteApiKey = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (keyId: string) => {
            await api.delete(`/api-keys/${keyId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
            toast.success('API Key deleted successfully');
        },
    });
};

// New SDK Usage hook (replaces SDK Rate Limit)
export const useSdkUsage = () => {
    return useQuery({
        queryKey: ['sdkUsage'],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; data: SdkUsageResponse }>('/organization/sdk-usage');
            return data.data;
        },
        refetchInterval: 30000, // Refetch every 30 seconds for real-time data
    });
};
